/**
 * Gestionnaire de tokens JWT cote client.
 * Decode, verifie l'expiration, gere le stockage via tokenStorage.
 *
 * Ne verifie PAS la signature (c'est le role du backend).
 */

import { tokenStorage } from '../storage/tokenStorage';

export interface JWTPayload {
  idUtilisateur: string;
  email: string;
  role: string;
  iat: number;
  exp: number;
}

export class TokenManager {
  decode(token: string): JWTPayload | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      return JSON.parse(atob(parts[1])) as JWTPayload;
    } catch {
      return null;
    }
  }

  isExpired(token: string): boolean {
    const payload = this.decode(token);
    if (!payload?.exp) return true;
    const now = Math.floor(Date.now() / 1000);
    return payload.exp < (now + 30);
  }

  getTimeToExpiry(token: string): number | null {
    const payload = this.decode(token);
    if (!payload?.exp) return null;
    const remaining = payload.exp - Math.floor(Date.now() / 1000);
    return remaining > 0 ? remaining : 0;
  }

  needsRefresh(token: string, marginSeconds: number = 300): boolean {
    const timeToExpiry = this.getTimeToExpiry(token);
    if (timeToExpiry === null) return true;
    return timeToExpiry < marginSeconds;
  }

  getAccessToken(): string | null {
    return tokenStorage.getAccessToken();
  }

  setAccessToken(accessToken: string): void {
    tokenStorage.setAccessToken(accessToken);
  }

  clearTokens(): void {
    tokenStorage.clearTokens();
  }

  isAuthenticated(): boolean {
    const token = this.getAccessToken();
    if (!token) return false;
    return !this.isExpired(token);
  }

  getUserInfo(): Omit<JWTPayload, 'iat' | 'exp'> | null {
    const token = this.getAccessToken();
    if (!token) return null;
    const payload = this.decode(token);
    if (!payload) return null;
    return {
      idUtilisateur: payload.idUtilisateur,
      email: payload.email,
      role: payload.role,
    };
  }

  getUserRole(): string | null {
    return this.getUserInfo()?.role || null;
  }

  hasRole(role: string): boolean {
    return this.getUserRole() === role;
  }

  hasAnyRole(roles: string[]): boolean {
    const userRole = this.getUserRole();
    return userRole !== null && roles.includes(userRole);
  }

  getAuthorizationHeader(): string | null {
    const token = this.getAccessToken();
    if (!token) return null;
    return `Bearer ${token}`;
  }

  formatTimeToExpiry(token: string): string {
    const seconds = this.getTimeToExpiry(token);
    if (seconds === null || seconds <= 0) return 'Expiré';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${secs}s`;
    return `${secs}s`;
  }
}

/** Instance singleton */
export const tokenManager = new TokenManager();

/** Helper : verifier si l'utilisateur est authentifie */
export function isAuthenticated(): boolean {
  return tokenManager.isAuthenticated();
}

/** Helper : recuperer les infos utilisateur du token */
export function getCurrentUser(): Omit<JWTPayload, 'iat' | 'exp'> | null {
  return tokenManager.getUserInfo();
}
